import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, LayersIcon, LinkIcon, KeyRoundIcon, HashIcon, BoxIcon } from 'lucide-react';
export interface FlowDef {
  id: string;
  name: string;
  description: string;
  icon: BoxIcon;
  keyNumber: number;
}
interface FlowSwitcherProps {
  flows: FlowDef[];
  activeFlowId: string;
  onSelectFlow: (id: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}
export function FlowSwitcher({
  flows,
  activeFlowId,
  onSelectFlow,
  isOpen,
  onToggle,
  onClose
}: FlowSwitcherProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeFlow = flows.find(f => f.id === activeFlowId) || flows[0];
  const ActiveIcon = activeFlow.icon;
  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);
  return <div ref={containerRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-id="element-1">
      {/* Dropdown Panel */}
      <AnimatePresence data-id="element-2">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="mb-3 bg-slate-800/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl overflow-hidden min-w-[320px]" data-id="element-3">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5" data-id="element-4">
              <span className="text-white font-medium" data-id="element-5">Onboarding Flow</span>
              <span className="text-white/40 text-sm" data-id="element-6">Press 1-4</span>
            </div>

            {/* Flow Options */}
            <div className="py-1" data-id="element-7">
              {flows.map(flow => {
            const FlowIcon = flow.icon;
            const isActive = flow.id === activeFlowId;
            return <button key={flow.id} onClick={() => {
              onSelectFlow(flow.id);
              onClose();
            }} className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${isActive ? 'bg-cyan-500/10' : 'hover:bg-white/5'}`} data-id="element-8">
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive ? 'bg-cyan-500' : 'bg-slate-700/80'}`} data-id="element-9">
                      <FlowIcon className="w-5 h-5 text-white" data-id="element-10" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 text-left" data-id="element-11">
                      <div className={`font-medium ${isActive ? 'text-cyan-400' : 'text-white'}`} data-id="element-12">
                        {flow.name}
                      </div>
                      <div className="text-white/50 text-sm" data-id="element-13">
                        {flow.description}
                      </div>
                    </div>

                    {/* Right side: checkmark or number */}
                    {isActive ? <CheckIcon className="w-5 h-5 text-cyan-500" data-id="element-14" /> : <span className="w-6 h-6 rounded bg-slate-700/80 flex items-center justify-center text-white/50 text-sm font-medium" data-id="element-15">
                        {flow.keyNumber}
                      </span>}
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Floating Pill Button */}
      <motion.button onClick={onToggle} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className={`flex items-center gap-3 px-4 py-2.5 rounded-full border shadow-lg transition-colors ${isOpen ? 'bg-cyan-600 border-cyan-500' : 'bg-slate-800/95 backdrop-blur-xl border-white/10 hover:bg-slate-700/95'}`} data-id="element-16">
        <ActiveIcon className="w-5 h-5 text-white" data-id="element-17" />
        <span className="text-white font-medium" data-id="element-18">{activeFlow.name}</span>
        <span className="w-6 h-6 rounded bg-slate-700/80 flex items-center justify-center text-white/60 text-sm font-medium" data-id="element-19">
          {activeFlow.keyNumber}
        </span>
      </motion.button>
    </div>;
}
// Export default flow definitions for convenience
export const defaultFlows: FlowDef[] = [{
  id: 'current',
  name: 'Current Flow',
  description: 'Standard onboarding',
  icon: LayersIcon,
  keyNumber: 1
}, {
  id: 'magiclink',
  name: 'Magic Link',
  description: 'One-click email login',
  icon: LinkIcon,
  keyNumber: 2
}, {
  id: 'otp',
  name: 'OTP Code',
  description: '6-digit email code',
  icon: KeyRoundIcon,
  keyNumber: 3
}, {
  id: 'pin',
  name: 'Persistent PIN',
  description: 'Generated home code',
  icon: HashIcon,
  keyNumber: 4
}];